import React, { useMemo, useRef, useState } from "react"
import Input from "../../../Ui/Form/Input"
import { Container, InputContainer, Label, ShowMoreButton } from "./DisplayNameInput.styled"
import PrimaryButton from "../../../Ui/Buttons/PrimaryButton"
import { UseFormSetValue } from "react-hook-form/dist/types/form"
import {
  InputCreateOrUpdateDiscountCodeRule,
  InputCreateOrUpdateDiscountExternalRule,
  InputCreateOrUpdateDiscountRule
} from "lib/types/generated/graphql-types"
import { FieldValues, Path, PathValue, UseFormWatch } from "react-hook-form"
import { Tooltip } from "../../../Ui/Tooltip"
import ISO6391 from "iso-639-1"
import SingleSelectSimple from "../../../Ui/Form/SingleSelectSimple"
import DisplayNameRow from "./DisplayNameRow"
import { WithAuth } from "../../../Auth"
import { PermissionGroups } from "../../../../routes/constants"

interface ExtendedInputCreateOrUpdateDiscountCodeRule extends InputCreateOrUpdateDiscountCodeRule {
  id?: string
}

type DisplayName = {
  languageCode: string
  translation: string
}

type Props<T extends FieldValues> = {
  setValue: UseFormSetValue<T>
  watch: UseFormWatch<T>
  savedDisplayNames?: DisplayName[]
}

const DisplayNameInput = <
  T extends
    | InputCreateOrUpdateDiscountRule
    | InputCreateOrUpdateDiscountCodeRule
    | InputCreateOrUpdateDiscountExternalRule
    | (ExtendedInputCreateOrUpdateDiscountCodeRule & FieldValues)
>({
  setValue,
  watch,
  savedDisplayNames
}: Props<T>) => {
  const initialValue = { languageCode: "", translation: "" }
  const fieldName = "displayNames" as Path<T>
  const keyInputRef = useRef<HTMLInputElement>(null)

  const [expandDisplayNames, setExpandDisplayNames] = useState(false)
  const [displayName, setDisplayName] = useState<DisplayName>(initialValue)
  const displayNames = (watch(fieldName) ?? []) as DisplayName[]

  const isAddDisabled = !displayName.languageCode || !displayName.translation

  const preferredLanguages = ["sv", "en", "da", "nb", "fi"]

  const getSortedLanguageOptions = (displayNames: DisplayName[]) => {
    return ISO6391.getAllCodes()
      .filter((code) => !displayNames.some((d) => d.languageCode === code))
      .map((code) => ({
        label: `${ISO6391.getName(code)} (${code})`,
        value: code
      }))
      .sort((a, b) => {
        const aIndex = preferredLanguages.indexOf(a.value)
        const bIndex = preferredLanguages.indexOf(b.value)

        if (aIndex > -1 && bIndex > -1) return aIndex - bIndex
        if (aIndex > -1) return -1
        if (bIndex > -1) return 1
        return a.label.localeCompare(b.label)
      })
  }
  const languageCodeOption = useMemo(() => getSortedLanguageOptions(displayNames), [displayNames])

  const addDisplayName = () => {
    if (displayName.languageCode && displayName.translation) {
      setValue(fieldName, [displayName, ...displayNames] as PathValue<T, Path<T>>)
      setDisplayName(initialValue)
      keyInputRef.current?.focus()
    }
  }

  const updateTranslation = (languageCode: string, newTranslation: string) => {
    const updated = displayNames.map((item) =>
      item.languageCode === languageCode ? { ...item, translation: newTranslation } : item
    )
    const sorted = [
      ...updated.filter((item) => item.languageCode === languageCode),
      ...updated.filter((item) => item.languageCode !== languageCode)
    ]
    setValue(fieldName, sorted as PathValue<T, Path<T>>)
  }

  const removeDisplayName = (key: string) => {
    setValue(fieldName, displayNames.filter((d) => d.languageCode !== key) as PathValue<T, Path<T>>)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isAddDisabled) {
      e.preventDefault()
      addDisplayName()
    }
  }

  return (
    <Container>
      <Label>Display names</Label>
      <p>Display names used in shopper sessions (optional) </p>
      <WithAuth requiredGroups={PermissionGroups.discounts.write}>
        <InputContainer>
          <SingleSelectSimple
            options={languageCodeOption}
            isSearchable={true}
            placeholder={"Language Code"}
            value={
              displayName.languageCode
                ? {
                  label: `${ISO6391.getName(displayName.languageCode)} (${displayName.languageCode})`,
                  value: displayName.languageCode
                }
                : null
            }
            setValue={(languageCode) =>
              setDisplayName({
                ...displayName,
                languageCode: languageCode
              })
            }
          />
          <Input
            name="value"
            placeholder={"Translation"}
            onKeyDown={handleKeyDown}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              setDisplayName({ ...displayName, translation: e.currentTarget.value })
            }
            value={displayName.translation}
          />
          <PrimaryButton type={"button"} handleClick={addDisplayName} disabled={isAddDisabled}>
            Add
          </PrimaryButton>
        </InputContainer>
      </WithAuth>
      <Tooltip id={"unsaved"}></Tooltip>
      {displayNames.length > 0 &&
        displayNames
          .slice(0, 5)
          .map((displayName) => (
            <DisplayNameRow
              displayName={displayName}
              key={displayName.languageCode}
              savedDisplayNames={savedDisplayNames}
              removeDisplayName={removeDisplayName}
              updateTranslation={updateTranslation}
            />
          ))}
      {!expandDisplayNames && displayNames.length > 5 && (
        <ShowMoreButton onClick={() => setExpandDisplayNames(true)}>
          Show more ({displayNames.length - 5})
        </ShowMoreButton>
      )}
      {expandDisplayNames &&
        displayNames
          .slice(5)
          .map((displayName) => (
            <DisplayNameRow
              displayName={displayName}
              key={displayName.languageCode}
              savedDisplayNames={savedDisplayNames}
              removeDisplayName={removeDisplayName}
              updateTranslation={updateTranslation}
            />
          ))}
    </Container>
  )
}

export default DisplayNameInput
